import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { BookMarked, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const FaithTeachings = () => {
  const teachings = [
    {
      id: 1,
      title: "Can Good Deeds Get You Into Heaven?",
      scripture: "Ephesians 2:8-10",
      description: "The Bible's answer may surprise you. Learn the difference between being saved by grace and being created for good works.",
      imageUrl: "https://customer-assets.emergentagent.com/job_faith-library-22/artifacts/3kyui59u_file_000000008d98720ba2f635bae2a728c6.png",
      tags: ["salvation", "grace", "good works", "ephesians"]
    },
    {
      id: 2,
      title: "Did Jesus Really Expect Us To Be Perfect?",
      scripture: "Matthew 5:48",
      description: "At first glance, this command seems impossible—but what did Jesus actually mean? The answer reveals God's amazing plan for your spiritual growth.",
      imageUrl: "https://customer-assets.emergentagent.com/job_faith-library-22/artifacts/uhgqxa2h_file_0000000019f872078bd07a38c1efcfc1.png",
      tags: ["perfection", "spiritual growth", "matthew", "holiness"]
    },
    {
      id: 3,
      title: "Is There Anything God Won't Forgive?",
      scripture: "1 John 1:9",
      description: "The answer may give you hope today. No matter your past, God's mercy is greater than your failures when you come to Him with a sincere heart.",
      imageUrl: "https://customer-assets.emergentagent.com/job_faith-library-22/artifacts/jebfsr46_file_0000000001047209b2236bd8b1a31b2e.png",
      tags: ["forgiveness", "mercy", "grace", "hope"]
    },
    {
      id: 4,
      title: "Finding Rest in Jesus",
      scripture: "Matthew 11:28-30",
      description: "What if the rest you've been searching for isn't found in a vacation... but in a Person? Jesus offers a kind of rest that no amount of sleep, money, or success can provide.",
      imageUrl: "https://customer-assets.emergentagent.com/job_faith-library-22/artifacts/ul8i8dbf_.trashed-1785456593-file_00000000b710720b86858fc69faafa2d.png",
      tags: ["rest", "peace", "jesus", "matthew"]
    }
  ];

  const handleShare = (teaching) => {
    if (navigator.share) {
      navigator.share({
        title: teaching.title,
        text: teaching.description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleDownload = (imageUrl, title) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `${title.replace(/[^a-z0-9]/gi, '_')}.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Downloading image...');
  };

  return (
    <div className="min-h-screen bg-background-primary">
      <Navbar />

      {/* Banner */}
      <section 
        className="relative h-96 flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/13036981/pexels-photo-13036981.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="faith-teachings-banner"
      >
        <div className="absolute inset-0 bg-brand-primary/85"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <BookMarked className="h-16 w-16 text-brand-accent mx-auto mb-6" />
          <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter leading-tight text-white mb-4" data-testid="faith-teachings-title">
            Faith Teachings
          </h1>
          <p className="font-body text-lg text-white/90 max-w-2xl mx-auto">
            Powerful biblical truths to strengthen your faith and deepen your understanding of God's grace
          </p>
        </div>
      </section>

      {/* Teachings Grid */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {teachings.map((teaching) => (
              <div 
                key={teaching.id}
                className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                data-testid={`teaching-card-${teaching.id}`}
              >
                {/* Image */}
                <div className="relative aspect-square bg-slate-100 overflow-hidden group cursor-pointer"
                     onClick={() => window.open(teaching.imageUrl, '_blank')}>
                  <img
                    src={teaching.imageUrl}
                    alt={teaching.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="inline-block bg-brand-accent/10 text-brand-accent border border-brand-accent/20 rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wider">
                      {teaching.scripture}
                    </span>
                  </div>
                  
                  <h3 className="font-heading text-2xl font-medium text-brand-primary mb-4">
                    {teaching.title}
                  </h3>
                  
                  <p className="font-body text-base leading-relaxed text-slate-700 mb-6">
                    {teaching.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {teaching.tags.map((tag, idx) => (
                      <span key={idx} className="text-xs font-body text-slate-600 bg-slate-100 px-2 py-1 rounded">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleDownload(teaching.imageUrl, teaching.title)}
                      className="flex-1 px-4 py-2 bg-brand-accent text-white font-body font-medium text-sm rounded hover:bg-brand-accent-hover transition-all duration-300"
                      data-testid={`download-teaching-${teaching.id}`}
                    >
                      Download
                    </button>
                    <button
                      onClick={() => handleShare(teaching)}
                      className="px-4 py-2 bg-transparent border border-slate-300 text-slate-700 font-body font-medium text-sm rounded hover:bg-slate-50 transition-all duration-300 inline-flex items-center space-x-2"
                      data-testid={`share-teaching-${teaching.id}`}
                    >
                      <Share2 className="h-4 w-4" />
                      <span>Share</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 md:py-32 bg-background-tertiary">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-brand-primary mb-6">
            Share These Truths
          </h2>
          <p className="font-body text-lg text-slate-700 mb-8">
            Download and share these graphics on social media to encourage others in their faith journey.
          </p>
          <p className="font-body text-base text-slate-600">
            READ. LEARN. GROW. SHARE THE WORD. Make an Impact. ❤️
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FaithTeachings;
